import { createContext, useContext, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { removeToken, setToken } from "@/api/api";
import type { Login, User } from "@/types/api/user";
import type { AuthContextType } from "@/types/contexts/auth";

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  isLoading: true,
  user: null,
  setUser: () => {},
  login: () => {},
  logout: () => {},
});

const USER_STORAGE_KEY = "myPeriocare/userData";

interface AuthUserProviderProps {
  children: React.ReactNode;
}

const AuthProvider = ({ children }: AuthUserProviderProps) => {
  const queryClient = useQueryClient();

  const localUserData = localStorage.getItem(USER_STORAGE_KEY);
  const parsedUserData = localUserData ? JSON.parse(localUserData) : null;

  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<User | null>(
    localUserData ? parsedUserData : null
  );

  const isAuthenticated = !!user;

  const login = (data: Login) => {
    const { token, user: userData } = data;
    setUserState(userData);
    setToken(token);
    setIsLoading(false);
  };

  const setUserState = (userData: User | null) => {
    setUser(userData);
    if (!userData) {
      localStorage.removeItem(USER_STORAGE_KEY);
    } else {
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(userData));
    }
    setIsLoading(false);
  };

  const logout = () => {
    setUserState(null);
    removeToken();
    queryClient.clear();
    setIsLoading(false);
  };

  useEffect(() => {
    const storedUserData = localStorage.getItem(USER_STORAGE_KEY);
    if (storedUserData) {
      const parsedStoredUser = JSON.parse(storedUserData);
      setUserState(parsedStoredUser);
    } else {
      setIsLoading(false);
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        user,
        setUser,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => {
  return useContext(AuthContext);
};

const useUser = () => {
  const user = useAuth().user;
  if (!user) {
    throw new Error("Utilisateur non connecté");
  }

  return user;
};

export { AuthProvider, AuthContext, useAuth, useUser };
